import { AnomalyCsvImportResume } from '@api/twin/AnomalyApi';
import { NotificationCenter } from '@stereograph/teiaviewer';
import { usePluginTranslation } from '@translation';
import { useGetFormattedCsvImportsErrors } from './useGetFormattedCsvImportsErrors';

export const useDisplayAnomalyCsvImportNotification = () => {
  const { t } = usePluginTranslation();
  const { getFormattedCsvImportsErrors } = useGetFormattedCsvImportsErrors();

  const displayAnomalyCsvImportNotification = (resumes: Array<AnomalyCsvImportResume>) => {
    const totalAdded = resumes.reduce((sum, resume) => sum + (resume?.addedCount ?? 0), 0);
    const totalUpdated = resumes.reduce((sum, resume) => sum + (resume?.updatedCount ?? 0), 0);

    const allFailedEntries = resumes.flatMap((resume) => Object.entries(resume?.failed ?? {}));
    const hasNoChange = totalAdded === 0 && totalUpdated === 0 && allFailedEntries.length === 0;

    if (!resumes || resumes.length === 0 || hasNoChange) {
      NotificationCenter.instance.pushNotification({
        type: t('anomaly_plugin.notification_type_text'),
        status: 'warning',
        message: t('anomaly_plugin.info_widget.upload.excel_import_warning_text'),
        title: t('anomaly_plugin.info_widget.upload.notification_title'),
        displayed: true,
      });

      return;
    }

    const errorSectionsByFile: Array<string> = resumes
      .map((resume) => {
        const entries = Object.entries(resume?.failed ?? {});
        if (entries.length === 0) {
          return '';
        }

        const fileLabel = `${resume.filename}`;
        const lines = entries.map(([line, errorCode]) =>
          `${t('anomaly_plugin.info_widget.upload.excel_import_failed_text', {
            count: parseInt(line, 10),
          })} : ${getFormattedCsvImportsErrors(errorCode)}`,
        );

        return `${fileLabel}\n${lines.join('\n')}`;
      })
      .filter(Boolean);

    const hasImportErrors = errorSectionsByFile.length > 0;
    const hasImportFailed = totalAdded === 0 && totalUpdated === 0 && hasImportErrors;

    const summary
      = `${t('anomaly_plugin.info_widget.upload.excel_created_anomaly.label', {
        count: totalAdded,
      })}, `
      + `${t('anomaly_plugin.info_widget.upload.excel_updated_anomaly.label', {
        count: totalUpdated,
      })}`;

    const errorMessage = errorSectionsByFile.join('\n\n');

    const successMessage = hasImportErrors ? `${summary}\n\n${errorMessage}` : summary;

    if (hasImportFailed) {
      return NotificationCenter.instance.pushNotification({
        type: t('anomaly_plugin.notification_type_text'),
        status: 'error',
        message: errorMessage,
        title: t('anomaly_plugin.info_widget.upload.notification_title'),
        displayed: true,
      });
    }

    return NotificationCenter.instance.pushNotification({
      type: t('anomaly_plugin.notification_type_text'),
      status: 'success',
      message: successMessage,
      title: t('anomaly_plugin.info_widget.upload.notification_title'),
      displayed: true,
    });
  };

  return {
    displayAnomalyCsvImportNotification,
  };
};
